import { CircuitDayFormData } from '@/types/circuits/CircuitDaysFormData';
import { PropsWithChildren, useMemo, useState } from 'react';
import { RadioGroupItem } from '@/components/ui/radio-group';
import {
  formatPrice,
  roundUpPrice,
  sanitizeDeltaPrice,
} from '@/utils/circuits/prices/formatPrice';
import { useGetFileUrl } from '@/hooks/useFile';
import EstimateBenefitImagesModal from '../EstimateBenefitImagesModal';
import StyledExpandableText from '@/components/styled/StyledExpandableText';
import { useImageCalculatedHeight } from '@/hooks/useImageCalculatedHeight';
import StyledButton from '@/components/styled/StyledButton';
import { formatCurrency } from '@/utils/prices/formatCurrency';
import { useConvertPriceOnlyIfDateISProvided } from '@/hooks/useCurrencyExchange';
import { useTranslation } from '@/i18n';
import { SupportedLocale } from '@/types/supported-locale.type';

export interface EstimateOptionalBenefitCellProps {
  benefit: CircuitDayFormData['steps'][number]['transport']['optionalBenefits'][number];
  deltaPrice: number;
  isChecked: boolean;
  selectOptionalBenefit: () => void;
  optionalDisplays: Array<'name' | 'shortDescription' | 'longDescription'>;
  nbParticipants?: number;
  currency?: string;
  creationDate?: Date;
  accessToken?: string;
  mainImageKey?: string;
  imagesKeys?: string[];
  language?: SupportedLocale;
}

const EstimateOptionalBenefitCell = ({
  benefit,
  deltaPrice,
  isChecked,
  selectOptionalBenefit,
  optionalDisplays,
  nbParticipants,
  currency = 'EUR',
  creationDate,
  accessToken,
  mainImageKey,
  imagesKeys = [],
  language,
  children,
}: PropsWithChildren<EstimateOptionalBenefitCellProps>) => {
  const { t } = useTranslation();
  const [isImagesModalOpen, setIsImagesModalOpen] = useState(false);
  const convertPrice = useConvertPriceOnlyIfDateISProvided(creationDate);

  const { imgRef, calculatedHeight } = useImageCalculatedHeight({
    defaultHeight: 150,
  });

  const displayedDeltaPrice = useMemo(() => {
    const sanitized = sanitizeDeltaPrice(deltaPrice);
    const converted =
      convertPrice(sanitized, 'EUR', currency) ?? sanitized;
    const displayAmount = roundUpPrice(converted);
    return `${displayAmount >= 0 ? '+' : '-'} ${formatPrice(Math.abs(displayAmount))} ${formatCurrency(currency)}`;
  }, [convertPrice, currency, deltaPrice]);

  const shortDescription = useMemo(() => {
    const shouldDisplayShortDescription =
      optionalDisplays.includes('shortDescription');

    const translations = language
      ? benefit?.translations?.[language]
      : undefined;

    return shouldDisplayShortDescription
      ? (translations?.activity?.shortDescription ??
          translations?.flight?.shortDescription ??
          translations?.VehicleWithChauffeur?.shortDescription ??
          translations?.hotel?.shortDescriptionBody ??
          benefit?.activity?.shortDescription ??
          benefit?.flight?.shortDescription ??
          benefit?.VehicleWithChauffeur?.shortDescription ??
          benefit?.hotel?.shortDescriptionBody ??
          '')
      : null;
  }, [
    benefit?.VehicleWithChauffeur?.shortDescription,
    benefit?.activity?.shortDescription,
    benefit?.flight?.shortDescription,
    benefit?.hotel?.shortDescriptionBody,
    benefit?.translations,
    language,
    optionalDisplays,
  ]);

  const longDescription = useMemo(() => {
    const shouldDisplayLongDescription =
      optionalDisplays.includes('longDescription');

    const translations = language
      ? benefit?.translations?.[language]
      : undefined;

    return shouldDisplayLongDescription
      ? (translations?.activity?.longDescription ??
          translations?.flight?.longDescription ??
          translations?.VehicleWithChauffeur?.longDescription ??
          translations?.hotel?.longDescriptionBody ??
          benefit?.activity?.longDescription ??
          benefit?.flight?.longDescription ??
          benefit?.VehicleWithChauffeur?.longDescription ??
          benefit?.hotel?.longDescriptionBody ??
          '')
      : '';
  }, [
    benefit?.VehicleWithChauffeur?.longDescription,
    benefit?.activity?.longDescription,
    benefit?.flight?.longDescription,
    benefit?.hotel?.longDescriptionBody,
    benefit?.translations,
    language,
    optionalDisplays,
  ]);

  const { data: imageUrl } = useGetFileUrl(
    mainImageKey ?? null,
    false,
    accessToken,
  );

  const vehicleWithChauffeurNbCars = useMemo(() => {
    if (!nbParticipants) return null;
    const capacity = benefit?.VehicleWithChauffeur?.maxCapacity ?? null;
    if (!capacity) return null;
    return Math.ceil(nbParticipants / capacity);
  }, [nbParticipants, benefit?.VehicleWithChauffeur?.maxCapacity]);

  const displayName = useMemo(() => {
    const translations = language
      ? benefit?.translations?.[language]
      : undefined;
    const stepName = translations?.name ?? benefit?.name;
    return optionalDisplays.includes('name')
      ? vehicleWithChauffeurNbCars
        ? `${vehicleWithChauffeurNbCars} × ${stepName}`
        : stepName
      : null;
  }, [
    benefit?.name,
    benefit?.translations,
    language,
    optionalDisplays,
    vehicleWithChauffeurNbCars,
  ]);

  return benefit && optionalDisplays.length > 0 ? (
    <div className="flex flex-col gap-4">
      <div className="flex w-full flex-col gap-4 md:flex-row-reverse md:items-start">
        {imageUrl && (
          <div className="shrink-0 self-center md:self-start">
            <img
              src={imageUrl.url}
              ref={imgRef}
              onClick={() => {
                setIsImagesModalOpen(true);
              }}
              className="h-auto max-h-56 w-72 min-w-72 rounded-lg object-cover object-center hover:cursor-pointer lg:w-80 lg:min-w-80"
            />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-4">
            <RadioGroupItem
              value={benefit.id}
              onClick={() => {
                selectOptionalBenefit();
              }}
              checked={isChecked}
              disabled={deltaPrice === Infinity}
            />
            {displayName && <span>{displayName}</span>}
            <span className="text-sm font-semibold text-brand-700 whitespace-nowrap">
              {displayedDeltaPrice}
            </span>
          </div>
          {shortDescription && (
            <StyledExpandableText
              text={shortDescription}
              maxHeight={calculatedHeight - 50}
            />
          )}
          {(longDescription || imagesKeys.length > 0) && (
            <StyledButton
              title={t('estimates.details.dayStep.showDetail')}
              onClick={() => {
                setIsImagesModalOpen(true);
              }}
              variant="link"
              className="p-0 text-brand-700"
            />
          )}
        </div>
      </div>
      {children}

      {isImagesModalOpen && (
        <EstimateBenefitImagesModal
          closeModal={() => {
            setIsImagesModalOpen(false);
          }}
          title={displayName ?? benefit.name ?? ''}
          description={longDescription}
          imageKeys={imagesKeys}
          accessToken={accessToken}
        />
      )}
    </div>
  ) : null;
};

export default EstimateOptionalBenefitCell;
